import Cookies from "js-cookie";
import { lazy, Suspense } from "react";
import { RouterProvider, createBrowserRouter, Navigate, useLocation, redirect } from "react-router-dom";
import NotFound from "../pages/NotFound";
import RootLayout from "../pages/RootLayout";
import RootLayoutSigned from "../pages/RootLayoutSigned";
import { useAuth } from "../context/AuthContext";
import UserHome from "../pages/UserHome";
import RootLayoutUser from "../pages/RootLayoutUser";
import SurveysPage from "../pages/SurveysPage";
import SurveyManage from "../pages/SurveyManage";
import TakeSurvey from "../pages/TakeSurvey";
import APIUsageHistoryPage from "../pages/ApiUsageHistory";

// Lazy load only the heavy page components
const Users = lazy(() => import("../pages/Users"));
const UserPage = lazy(() => import("../pages/UserPage"));
const HomePage = lazy(() => import("../pages/HomePage"));
const LoginPage = lazy(() => import("../pages/LoginPage"));
const OrgsPages = lazy(() => import("../pages/OrgsPages"));
const UserSession = lazy(() => import("../pages/UserSession"));
const OrgDetailsPage = lazy(() => import("../pages/OrgDetailsPage"));
const MultiOrgReport = lazy(() => import("../pages/MultiOrgReport"));
const APIKeysManagement = lazy(() => import("../pages/APIKeysManagement"));
const ApiDocumentation = lazy(() => import("../pages/ApiDocumentation"));

// Loading component for Suspense fallback
export const PageLoader = () => (
  <div className="w-full h-full flex items-center justify-center">
    <div className="animate-spin rounded-full self-center h-12 w-12 border-b-2 border-orange-500"></div>
  </div>
);

// Wrapper component to handle Suspense
const LazyPage = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

const ProtectedRoute = ({ isAllowed, children }: { isAllowed?: string; children: React.ReactNode }) => {
  const access = !!Cookies.get("access");
  const location = useLocation();

  if (!access) {
    return <Navigate to={`/?backto=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  if (isAllowed && Cookies.get(isAllowed) !== "allowed") {
    return <Navigate to={"/notfound"} replace />;
  }

  // return <LazyPage>{children}</LazyPage>;
  return <LazyPage>{children}</LazyPage>;
};

const Routes = () => {
  const { orgId, role } = useAuth();

  const logRegRoutes = [
    //registration
    {
      element: <RootLayout />,
      children: [
        {
          path: "/",
          element: (
            <LazyPage>
              <LoginPage />
            </LazyPage>
          ),
        },
      ],
    },
  ];

  const logedInRoutes = [
    {
      element: <RootLayoutSigned />,
      children: [
        {
          path: "/home",
          element: <ProtectedRoute children={<HomePage />} />,
        },
        {
          path: "/orgs",
          element: <ProtectedRoute children={<OrgsPages />} />,
        },
        {
          path: "/org-details/:orgID",
          element: <ProtectedRoute children={<OrgDetailsPage />} />,
        },
        {
          path: "/users",
          element: <ProtectedRoute children={<Users />} />,
        },
        {
          path: "/user",
          element: <ProtectedRoute children={<UserPage />} />,
        },
        {
          path: "/session",
          element: <ProtectedRoute children={<UserSession />} />,
        },
        {
          path: "/surveys",
          element: <ProtectedRoute children={<SurveysPage />} />,
        },
        {
          path: "/surveys/manage",
          element: <ProtectedRoute children={<SurveyManage />} />,
        },
        {
          path: "/api",
          element: <ProtectedRoute children={<APIKeysManagement />} />,
        },
        {
          path: "/api/docs",
          element: <ProtectedRoute children={<ApiDocumentation />} />,
        },
        {
          path: "/api-logs",
          element: <ProtectedRoute children={<APIUsageHistoryPage />} />,
        },
        {
          path: "/multi-report",
          element: <ProtectedRoute children={<MultiOrgReport />} />,
        },
      ],
    },
  ];

  const logedUser = [
    {
      element: <RootLayoutUser />,
      children: [
        {
          path: "/home",
          element: <ProtectedRoute children={<UserHome />} />,
        },
        {
          path: "/session",
          element: <ProtectedRoute children={<UserSession />} />,
        },
        {
          path: "/session/survey",
          element: <ProtectedRoute children={<TakeSurvey />} />,
        },
      ],
    },
  ];

  const logedOrgAdmin = [
    {
      element: <RootLayoutSigned />,
      children: [
        {
          path: "/home",
          element: <ProtectedRoute children={<HomePage />} />,
        },
        {
          path: "/orgs",
          element: <Navigate to={`/orgs/details`} replace />,
        },
        {
          path: `/orgs/details`,
          element: <ProtectedRoute children={<OrgDetailsPage />} />,
        },
        {
          path: "/users",
          element: <ProtectedRoute children={<Users />} />,
        },
        {
          path: "/user",
          element: <ProtectedRoute children={<UserPage />} />,
        },
        {
          path: "/users",
          element: <ProtectedRoute children={<Users />} />,
        },
        {
          path: "/session",
          element: <ProtectedRoute children={<UserSession />} />,
        },
        {
          path: "/api",
          element: <ProtectedRoute children={<APIKeysManagement />} />,
        },
        {
          path: "/api/docs",
          element: <ProtectedRoute children={<ApiDocumentation />} />,
        },
        {
          path: "/api-logs",
          element: <ProtectedRoute children={<APIUsageHistoryPage />} />,
        },
      ],
    },
  ];

  const router = createBrowserRouter([
    ...logRegRoutes,
    ...(role === "super_admin" && Number(orgId) === 1 ? logedInRoutes : []),
    ...(role === "org_admin" ? logedOrgAdmin : []),
    ...(role === "user" ? logedUser : []),
    {
      element: <RootLayout />,
      children: [
        {
          path: "*",
          element: (
            <LazyPage>
              <ProtectedRoute children={<NotFound />} />,
            </LazyPage>
          ),
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
};

export default Routes;
